import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { FileText, Download, Edit, X, Save, Target, TrendingUp, Rocket, BarChart3 } from 'lucide-react';

const BusinessPlanTab = ({ authAxios }) => {
  const [plan, setPlan] = useState(null);
  const [editing, setEditing] = useState(null); // 'overview' | 'short' | 'medium' | 'long' | 'kpis'
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    fetchPlan();
  }, [authAxios]);

  const fetchPlan = async () => {
    try {
      const res = await authAxios.get('/admin/business-plan');
      setPlan(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put('/admin/business-plan', plan);
      toast.success('Business plan saved');
      setEditing(null);
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDownloadPDF = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Aureate Residences - Business Plan</title><style>
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a1a1a; margin: 0; padding: 40px; line-height: 1.6; }
      .header { text-align: center; border-bottom: 3px solid #D4AF37; padding-bottom: 24px; margin-bottom: 32px; }
      .header h1 { font-size: 28px; letter-spacing: 3px; margin: 0 0 4px; }
      .header .tagline { color: #D4AF37; font-size: 14px; }
      .header .date { color: #999; font-size: 11px; margin-top: 8px; }
      h2 { font-size: 16px; color: #D4AF37; text-transform: uppercase; letter-spacing: 2px; border-bottom: 1px solid #eee; padding-bottom: 8px; margin-top: 32px; }
      .overview { font-size: 14px; color: #444; }
      .mission-vision { display: flex; gap: 24px; margin: 16px 0; }
      .mv-box { flex: 1; background: #f9f9f9; padding: 16px; border-left: 3px solid #D4AF37; }
      .mv-box strong { display: block; font-size: 11px; text-transform: uppercase; color: #D4AF37; letter-spacing: 1px; margin-bottom: 4px; }
      .strategy-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 12px; }
      .strategy-table th { background: #0A0A0A; color: #D4AF37; padding: 10px 12px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
      .strategy-table td { padding: 10px 12px; border-bottom: 1px solid #eee; vertical-align: top; }
      .strategy-table tr:nth-child(even) td { background: #fafafa; }
      .kpi-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 12px; }
      .kpi-table th { background: #0A0A0A; color: #D4AF37; padding: 10px 12px; text-align: center; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; }
      .kpi-table th:first-child { text-align: left; }
      .kpi-table td { padding: 10px 12px; border-bottom: 1px solid #eee; text-align: center; }
      .kpi-table td:first-child { text-align: left; font-weight: 600; }
      .footer { margin-top: 40px; padding-top: 16px; border-top: 2px solid #D4AF37; text-align: center; font-size: 11px; color: #999; }
    </style></head><body>`);
    printWindow.document.write(`<div class="header"><h1>AUREATE RESIDENCES</h1><div class="tagline">Business Plan & Growth Strategy</div><div class="date">Last updated: ${plan.last_updated ? new Date(plan.last_updated).toLocaleDateString('en-GB', {day:'2-digit',month:'long',year:'numeric'}) : 'N/A'}</div></div>`);
    printWindow.document.write(`<div class="overview">${plan.company_overview}</div>`);
    printWindow.document.write(`<div class="mission-vision"><div class="mv-box"><strong>Mission</strong>${plan.mission}</div><div class="mv-box"><strong>Vision</strong>${plan.vision}</div></div>`);
    ['short_term', 'medium_term', 'long_term'].forEach(key => {
      const section = plan[key];
      printWindow.document.write(`<h2>${section.title}</h2><table class="strategy-table"><tr><th style="width:55%">Strategy</th><th>Expected Outcome</th></tr>`);
      section.strategies.forEach(s => {
        printWindow.document.write(`<tr><td>${s.action}</td><td>${s.outcome}</td></tr>`);
      });
      printWindow.document.write('</table>');
    });
    printWindow.document.write(`<h2>Key Performance Indicators</h2><table class="kpi-table"><tr><th>Metric</th><th>6 Months</th><th>18 Months</th><th>36 Months</th></tr>`);
    plan.kpis.forEach(k => {
      printWindow.document.write(`<tr><td>${k.metric}</td><td>${k.target_6m}</td><td>${k.target_18m}</td><td>${k.target_36m}</td></tr>`);
    });
    printWindow.document.write('</table>');
    printWindow.document.write(`<div class="footer">Aureate Residences Ltd. | Third Floor, 3 Hill Street, New Town, Edinburgh, EH2 3JP<br/>aureate-resident.com | info@aureate-resident.com</div>`);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  if (!plan) return <div className="text-center py-12 text-white/30 font-mono text-sm">Loading...</div>;

  const renderStrategySection = (key, icon, color) => {
    const section = plan[key];
    const isEditing = editing === key;
    return (
      <div className="bg-[#111111] border border-white/5 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            {icon}
            <h3 className="font-mono font-bold text-sm">{section.title}</h3>
          </div>
          <button onClick={() => setEditing(isEditing ? null : key)} className="px-3 py-1 bg-white/10 text-white hover:bg-white/20 font-mono text-xs flex items-center gap-1.5">
            <Edit className="w-3 h-3" /> {isEditing ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A0A0A]">
                <th className="px-6 py-3 text-left font-mono text-xs text-[#D4AF37] uppercase tracking-wider" style={{width: '55%'}}>Strategy</th>
                <th className="px-6 py-3 text-left font-mono text-xs text-[#D4AF37] uppercase tracking-wider">Expected Outcome</th>
                {isEditing && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {section.strategies.map((s, i) => (
                <tr key={i} className="hover:bg-white/[0.02]">
                  <td className="px-6 py-3 text-white/80">
                    {isEditing ? (
                      <input type="text" value={s.action} onChange={(e) => {
                        const updated = {...plan};
                        updated[key].strategies[i].action = e.target.value;
                        setPlan(updated);
                      }} className="w-full bg-transparent border border-white/10 px-3 py-1.5 text-sm focus:border-[#D4AF37] outline-none" />
                    ) : s.action}
                  </td>
                  <td className="px-6 py-3 text-white/50">
                    {isEditing ? (
                      <input type="text" value={s.outcome} onChange={(e) => {
                        const updated = {...plan};
                        updated[key].strategies[i].outcome = e.target.value;
                        setPlan(updated);
                      }} className="w-full bg-transparent border border-white/10 px-3 py-1.5 text-sm focus:border-[#D4AF37] outline-none" />
                    ) : s.outcome}
                  </td>
                  {isEditing && (
                    <td className="px-2">
                      <button onClick={() => {
                        const updated = {...plan};
                        updated[key].strategies.splice(i, 1);
                        setPlan({...updated});
                      }} className="text-red-400/50 hover:text-red-400 text-xs">x</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {isEditing && (
          <div className="px-6 py-3 border-t border-white/5 flex gap-2">
            <button onClick={() => {
              const updated = {...plan};
              updated[key].strategies.push({action: '', outcome: ''});
              setPlan({...updated});
            }} className="text-[#D4AF37] text-xs font-mono hover:underline">+ Add Strategy</button>
            <div className="ml-auto">
              <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-[#D4AF37] text-black font-mono text-xs font-bold hover:bg-[#D4AF37]/90 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="business-plan-tab" ref={printRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-mono font-bold text-lg">Business Plan & Growth Strategy</h2>
          {plan.last_updated && <p className="text-white/30 text-xs mt-1">Last updated: {new Date(plan.last_updated).toLocaleDateString('en-GB', {day:'2-digit', month:'short', year:'numeric'})}</p>}
        </div>
        <button onClick={handleDownloadPDF} data-testid="download-business-plan"
          className="px-4 py-2 bg-[#D4AF37] text-black font-mono text-sm font-bold flex items-center gap-2 hover:bg-[#D4AF37]/90">
          <Download className="w-4 h-4" /> Download PDF
        </button>
      </div>

      {/* Overview, Mission, Vision */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-mono font-bold text-sm text-[#D4AF37]">Company Overview</h3>
          <button onClick={() => setEditing(editing === 'overview' ? null : 'overview')} className="px-3 py-1 bg-white/10 text-white hover:bg-white/20 font-mono text-xs flex items-center gap-1.5">
            <Edit className="w-3 h-3" /> {editing === 'overview' ? 'Cancel' : 'Edit'}
          </button>
        </div>
        {editing === 'overview' ? (
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Overview</label>
              <textarea value={plan.company_overview} onChange={(e) => setPlan({...plan, company_overview: e.target.value})} rows={3}
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none resize-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Mission</label>
                <textarea value={plan.mission} onChange={(e) => setPlan({...plan, mission: e.target.value})} rows={2}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none resize-none" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-mono uppercase block mb-2">Vision</label>
                <textarea value={plan.vision} onChange={(e) => setPlan({...plan, vision: e.target.value})} rows={2}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none resize-none" />
              </div>
            </div>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-[#D4AF37] text-black font-mono text-xs font-bold hover:bg-[#D4AF37]/90 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        ) : (
          <>
            <p className="text-white/70 text-sm leading-relaxed mb-6">{plan.company_overview}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#0A0A0A] border-l-2 border-[#D4AF37]">
                <p className="text-[#D4AF37] text-xs font-mono uppercase tracking-wider mb-2">Mission</p>
                <p className="text-white/70 text-sm">{plan.mission}</p>
              </div>
              <div className="p-4 bg-[#0A0A0A] border-l-2 border-[#D4AF37]">
                <p className="text-[#D4AF37] text-xs font-mono uppercase tracking-wider mb-2">Vision</p>
                <p className="text-white/70 text-sm">{plan.vision}</p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Strategy Sections */}
      {renderStrategySection('short_term', <Target className="w-4 h-4 text-green-400" />)}
      {renderStrategySection('medium_term', <TrendingUp className="w-4 h-4 text-blue-400" />)}
      {renderStrategySection('long_term', <Rocket className="w-4 h-4 text-purple-400" />)}

      {/* KPIs */}
      <div className="bg-[#111111] border border-white/5 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-4 h-4 text-[#D4AF37]" />
            <h3 className="font-mono font-bold text-sm">Key Performance Indicators</h3>
          </div>
          <button onClick={() => setEditing(editing === 'kpis' ? null : 'kpis')} className="px-3 py-1 bg-white/10 text-white hover:bg-white/20 font-mono text-xs flex items-center gap-1.5">
            <Edit className="w-3 h-3" /> {editing === 'kpis' ? 'Cancel' : 'Edit'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A0A0A]">
                <th className="px-6 py-3 text-left font-mono text-xs text-[#D4AF37] uppercase tracking-wider">Metric</th>
                <th className="px-6 py-3 text-center font-mono text-xs text-[#D4AF37] uppercase tracking-wider">6 Months</th>
                <th className="px-6 py-3 text-center font-mono text-xs text-[#D4AF37] uppercase tracking-wider">18 Months</th>
                <th className="px-6 py-3 text-center font-mono text-xs text-[#D4AF37] uppercase tracking-wider">36 Months</th>
                {editing === 'kpis' && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {plan.kpis.map((k, i) => (
                <tr key={i} className="hover:bg-white/[0.02]">
                  <td className="px-6 py-3 font-medium text-white/80">
                    {editing === 'kpis' ? <input type="text" value={k.metric} onChange={(e) => { const u = {...plan}; u.kpis[i].metric = e.target.value; setPlan(u); }} className="w-full bg-transparent border border-white/10 px-2 py-1 text-sm focus:border-[#D4AF37] outline-none" /> : k.metric}
                  </td>
                  <td className="px-6 py-3 text-center text-white/50">
                    {editing === 'kpis' ? <input type="text" value={k.target_6m} onChange={(e) => { const u = {...plan}; u.kpis[i].target_6m = e.target.value; setPlan(u); }} className="w-full bg-transparent border border-white/10 px-2 py-1 text-sm text-center focus:border-[#D4AF37] outline-none" /> : k.target_6m}
                  </td>
                  <td className="px-6 py-3 text-center text-white/50">
                    {editing === 'kpis' ? <input type="text" value={k.target_18m} onChange={(e) => { const u = {...plan}; u.kpis[i].target_18m = e.target.value; setPlan(u); }} className="w-full bg-transparent border border-white/10 px-2 py-1 text-sm text-center focus:border-[#D4AF37] outline-none" /> : k.target_18m}
                  </td>
                  <td className="px-6 py-3 text-center text-white/50">
                    {editing === 'kpis' ? <input type="text" value={k.target_36m} onChange={(e) => { const u = {...plan}; u.kpis[i].target_36m = e.target.value; setPlan(u); }} className="w-full bg-transparent border border-white/10 px-2 py-1 text-sm text-center focus:border-[#D4AF37] outline-none" /> : k.target_36m}
                  </td>
                  {editing === 'kpis' && (
                    <td className="px-2"><button onClick={() => { const u = {...plan}; u.kpis.splice(i,1); setPlan({...u}); }} className="text-red-400/50 hover:text-red-400 text-xs">x</button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editing === 'kpis' && (
          <div className="px-6 py-3 border-t border-white/5 flex gap-2">
            <button onClick={() => { const u = {...plan}; u.kpis.push({metric:'',target_6m:'',target_18m:'',target_36m:''}); setPlan({...u}); }} className="text-[#D4AF37] text-xs font-mono hover:underline">+ Add KPI</button>
            <div className="ml-auto">
              <button onClick={handleSave} disabled={saving} className="px-4 py-1.5 bg-[#D4AF37] text-black font-mono text-xs font-bold hover:bg-[#D4AF37]/90 disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessPlanTab;
